// F-098 à F-101 : conditions d'un bail commercial ou professionnel, échéances, indexation, factures et versions.
// Utilisé par l'onglet « Conditions commerciales » du bail et par la fiche du module.
import { useEffect, useState } from "react";
import { patch, post, put } from "@core/api";
import { date, dateTime, money, percent, today } from "@core/format";
import { useGet, useInvalidate } from "@core/hooks";
import { useOrg } from "@core/org";
import { Button, Card, Checkbox, ErrorBox, Input, KeyValues, Loading, MoneyInput, Notice, Select, Table, Tabs, Textarea, fieldError } from "@core/ui";
import { EVENT_KINDS, REGIMES, RuleNotice, Status, docUrl, useQuick, useRun } from "./shared";

// ---------------------------------------------------------------------------
// Saisie des conditions

type Step = { key: number; from_month: string; rent_cents: number | null };
type Item = { category: string; label: string; borne_by: string };

export type TermsState = {
  tenant_company: string;
  tenant_legal_form: string;
  tenant_siren: string;
  representative_name: string;
  representative_capacity: string;
  activity: string;
  all_trades: boolean;
  duration_months: string;
  triennial_termination: boolean;
  payment_period: string;
  payment_term: string;
  index_kind: string;
  index_base_quarter: string;
  index_base_value: string;
  indexation_months: string;
  vat_option: boolean;
  vat_rate_bp: string;
  resolutory_clause: boolean;
  resolutory_delay_days: string;
  deposit_months: string;
  sublease_allowed: boolean;
  assignment_terms: string;
  rent_free_months: string;
  rent_steps: Step[];
  special_clauses: string;
  charges_inventory: Item[];
};

export function termsFromApi(t: any | null, defaults: Item[], regime: string): TermsState {
  return {
    tenant_company: t?.tenant_company ?? "",
    tenant_legal_form: t?.tenant_legal_form ?? "",
    tenant_siren: t?.tenant_siren ?? "",
    representative_name: t?.representative_name ?? "",
    representative_capacity: t?.representative_capacity ?? "",
    activity: t?.activity ?? "",
    all_trades: t?.all_trades ?? false,
    duration_months: String(t?.duration_months ?? (regime === "professionnel" ? 72 : 108)),
    triennial_termination: t?.triennial_termination ?? regime !== "professionnel",
    payment_period: t?.payment_period ?? "mensuel",
    payment_term: t?.payment_term ?? "echoir",
    index_kind: t?.index_kind ?? "aucun",
    index_base_quarter: t?.index_base_quarter ?? "",
    index_base_value: t?.index_base_value ?? "",
    indexation_months: String(t?.indexation_months ?? 12),
    vat_option: t?.vat_option ?? false,
    vat_rate_bp: String(t?.vat_rate_bp || 2000),
    resolutory_clause: t?.resolutory_clause ?? true,
    resolutory_delay_days: String(t?.resolutory_delay_days ?? 30),
    deposit_months: t?.deposit_months !== null && t?.deposit_months !== undefined ? String(t.deposit_months) : "",
    sublease_allowed: t?.sublease_allowed ?? false,
    assignment_terms: t?.assignment_terms ?? "",
    rent_free_months: String(t?.rent_free_months ?? 0),
    rent_steps: ((t?.rent_steps ?? []) as any[]).map((s, i) => ({ key: i, from_month: String(s.from_month), rent_cents: s.rent_cents })),
    special_clauses: t?.special_clauses ?? "",
    charges_inventory: (t?.charges_inventory as Item[] | undefined) ?? defaults,
  };
}

const int = (s: string) => (s.trim() === "" ? null : Number.isFinite(parseInt(s, 10)) ? parseInt(s, 10) : -1);

export function termsToBody(s: TermsState) {
  return {
    ...s,
    tenant_siren: s.tenant_siren || null,
    duration_months: int(s.duration_months),
    indexation_months: int(s.indexation_months) ?? 12,
    vat_rate_bp: s.vat_option ? int(s.vat_rate_bp) : 0,
    resolutory_delay_days: int(s.resolutory_delay_days) ?? 30,
    deposit_months: int(s.deposit_months),
    rent_free_months: int(s.rent_free_months) ?? 0,
    index_base_value: s.index_kind === "aucun" ? null : s.index_base_value,
    rent_steps: s.rent_steps.map((x) => ({ from_month: int(x.from_month) ?? 0, rent_cents: x.rent_cents ?? 0 })),
  };
}

export function TermsFields({ regime, value, onChange, error, disabled }: { regime: string; value: TermsState; onChange: (v: TermsState) => void; error: unknown; disabled?: boolean }) {
  const set = <K extends keyof TermsState>(k: K, v: TermsState[K]) => onChange({ ...value, [k]: v });
  const text = (k: keyof TermsState) => (e: { target: { value: string } }) => set(k, e.target.value as never);
  const commercial = regime !== "professionnel";
  return (
    <div className="stack">
      <h3>Preneur</h3>
      <div className="form-row">
        <Input label="Preneur (société ou professionnel)" required disabled={disabled} value={value.tenant_company} error={fieldError(error, "tenant_company")} onChange={text("tenant_company")} />
        <Input label="Forme juridique" disabled={disabled} value={value.tenant_legal_form} placeholder="SARL, SAS, EI…" onChange={text("tenant_legal_form")} />
        <Input label="SIREN" inputMode="numeric" disabled={disabled} value={value.tenant_siren} error={fieldError(error, "tenant_siren")} onChange={text("tenant_siren")} />
      </div>
      <div className="form-row">
        <Input label="Représentant signataire" disabled={disabled} value={value.representative_name} onChange={text("representative_name")} />
        <Input label="Qualité du signataire" disabled={disabled} value={value.representative_capacity} help="Gérant, président, mandataire… Joindre le justificatif de pouvoir (Kbis, statuts ou délégation)." onChange={text("representative_capacity")} />
      </div>
      <h3>Destination et durée</h3>
      <Textarea label="Destination des lieux (activités autorisées)" required disabled={disabled} value={value.activity} error={fieldError(error, "activity")} onChange={text("activity")} />
      <Checkbox label="Clause « tous commerces »" checked={value.all_trades} onChange={(v) => set("all_trades", v)} />
      <div className="form-row">
        <Input
          label="Durée (mois)"
          inputMode="numeric"
          disabled={disabled}
          value={value.duration_months}
          error={fieldError(error, "duration_months")}
          help={commercial ? "9 ans au moins (108 mois)." : "6 ans au moins (72 mois)."}
          onChange={text("duration_months")}
        />
        <Select label="Paiement" disabled={disabled} value={value.payment_period} options={[{ value: "mensuel", label: "Mensuel" }, { value: "trimestriel", label: "Trimestriel" }]} onChange={text("payment_period")} />
        <Select label="Terme" disabled={disabled} value={value.payment_term} options={[{ value: "echoir", label: "À échoir" }, { value: "echu", label: "À terme échu" }]} onChange={text("payment_term")} />
      </div>
      {commercial ? (
        <Checkbox
          label="Faculté de résiliation triennale du preneur"
          help="Le preneur peut donner congé à l’expiration de chaque période de trois ans, six mois à l’avance. Sa renonciation n’est possible que dans des cas prévus par la loi."
          checked={value.triennial_termination}
          onChange={(v) => set("triennial_termination", v)}
        />
      ) : (
        <Notice>Bail professionnel : le preneur peut quitter les lieux à tout moment avec un préavis de six mois ; pas de période triennale.</Notice>
      )}
      <h3>Loyer, TVA et indexation</h3>
      <div className="form-row">
        <Select
          label="Indice d’indexation"
          disabled={disabled}
          value={value.index_kind}
          error={fieldError(error, "index_kind")}
          help="ILC : activités commerciales et artisanales. ILAT : activités tertiaires (bureaux, professions libérales). Reprenez l’indice prévu au contrat."
          options={[
            { value: "aucun", label: "Aucune indexation" },
            { value: "ilc", label: "ILC — loyers commerciaux" },
            { value: "ilat", label: "ILAT — activités tertiaires" },
          ]}
          onChange={text("index_kind")}
        />
        {value.index_kind !== "aucun" ? (
          <>
            <Input label="Trimestre de référence" disabled={disabled} value={value.index_base_quarter} error={fieldError(error, "index_base_quarter")} placeholder="2026-T1" onChange={text("index_base_quarter")} />
            <Input label="Valeur de l’indice de base" inputMode="decimal" disabled={disabled} value={value.index_base_value} error={fieldError(error, "index_base_value")} help="Publiée par l’Insee." onChange={text("index_base_value")} />
            <Select label="Périodicité" disabled={disabled} value={value.indexation_months} options={[{ value: "12", label: "Annuelle" }, { value: "36", label: "Triennale" }]} onChange={text("indexation_months")} />
          </>
        ) : null}
      </div>
      <Checkbox label="Le bailleur a opté pour la TVA sur ce loyer" help="Ne cochez que si l’option est effective : la TVA n’est jamais présumée." checked={value.vat_option} onChange={(v) => set("vat_option", v)} />
      {value.vat_option ? (
        <Select
          label="Taux de TVA"
          disabled={disabled}
          value={value.vat_rate_bp}
          error={fieldError(error, "vat_rate_bp")}
          options={[
            { value: "2000", label: "20 %" },
            { value: "1000", label: "10 %" },
            { value: "550", label: "5,5 %" },
          ]}
          onChange={text("vat_rate_bp")}
        />
      ) : null}
      <div className="form-row">
        <Input label="Franchise de loyer (mois)" inputMode="numeric" disabled={disabled} value={value.rent_free_months} error={fieldError(error, "rent_free_months")} onChange={text("rent_free_months")} />
        <Input label="Dépôt de garantie (mois de loyer)" inputMode="numeric" disabled={disabled} value={value.deposit_months} error={fieldError(error, "deposit_months")} onChange={text("deposit_months")} />
      </div>
      <fieldset>
        <legend>Paliers de loyer (hors taxes, mensuel)</legend>
        {fieldError(error, "rent_steps") ? <p className="bv-champ-aide" role="alert">{fieldError(error, "rent_steps")}</p> : null}
        {value.rent_steps.map((s, i) => (
          <div key={s.key} className="pro-inline-form">
            <Input label="À partir du mois n°" inputMode="numeric" disabled={disabled} value={s.from_month} onChange={(e) => set("rent_steps", value.rent_steps.map((x, j) => (j === i ? { ...x, from_month: e.target.value } : x)))} />
            <MoneyInput label="Loyer du palier" cents={s.rent_cents} onChange={(c) => set("rent_steps", value.rent_steps.map((x, j) => (j === i ? { ...x, rent_cents: c } : x)))} />
            <Button variant="discret" small disabled={disabled} onClick={() => set("rent_steps", value.rent_steps.filter((_, j) => j !== i))}>
              Retirer
            </Button>
          </div>
        ))}
        <Button variant="discret" small disabled={disabled} onClick={() => set("rent_steps", [...value.rent_steps, { key: Date.now(), from_month: "", rent_cents: null }])}>
          Ajouter un palier
        </Button>
      </fieldset>
      <h3>Charges, impôts, taxes et travaux</h3>
      <RuleNotice>Inventaire proposé d’après le Code de commerce (art. L145-40-2 et R145-35) : certaines charges ne peuvent pas être imputées au preneur. Vérifiez la répartition pour ce bail.</RuleNotice>
      {fieldError(error, "charges_inventory") ? <p className="bv-champ-aide" role="alert">{fieldError(error, "charges_inventory")}</p> : null}
      <Table caption="Inventaire des charges">
        <thead>
          <tr>
            <th>Catégorie</th>
            <th>Poste</th>
            <th>À la charge de</th>
            <th />
          </tr>
        </thead>
        <tbody className="pro-steps-table">
          {value.charges_inventory.map((it, i) => (
            <tr key={i}>
              <td>
                <input aria-label="Catégorie" disabled={disabled} value={it.category} onChange={(e) => set("charges_inventory", value.charges_inventory.map((x, j) => (j === i ? { ...x, category: e.target.value } : x)))} />
              </td>
              <td>
                <input aria-label="Poste" disabled={disabled} value={it.label} onChange={(e) => set("charges_inventory", value.charges_inventory.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
              </td>
              <td>
                <select aria-label={`Répartition : ${it.label}`} disabled={disabled} value={it.borne_by} onChange={(e) => set("charges_inventory", value.charges_inventory.map((x, j) => (j === i ? { ...x, borne_by: e.target.value } : x)))}>
                  <option value="preneur">Preneur</option>
                  <option value="bailleur">Bailleur</option>
                  <option value="a_preciser">À préciser</option>
                </select>
              </td>
              <td>
                <Button variant="discret" small disabled={disabled} onClick={() => set("charges_inventory", value.charges_inventory.filter((_, j) => j !== i))}>
                  Retirer
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <div>
        <Button variant="discret" small disabled={disabled} onClick={() => set("charges_inventory", [...value.charges_inventory, { category: "Charges", label: "", borne_by: "a_preciser" }])}>
          Ajouter un poste
        </Button>
      </div>
      <h3>Autres clauses</h3>
      <Checkbox label="Clause résolutoire" checked={value.resolutory_clause} onChange={(v) => set("resolutory_clause", v)} />
      {value.resolutory_clause ? (
        <Input
          label="Délai après commandement (jours)"
          inputMode="numeric"
          disabled={disabled}
          value={value.resolutory_delay_days}
          error={fieldError(error, "resolutory_delay_days")}
          help="Un mois au moins (art. L145-41)."
          onChange={text("resolutory_delay_days")}
        />
      ) : null}
      <Checkbox label="Sous-location autorisée" checked={value.sublease_allowed} onChange={(v) => set("sublease_allowed", v)} />
      <Input label="Conditions de cession" disabled={disabled} value={value.assignment_terms} onChange={text("assignment_terms")} />
      <Textarea label="Clauses particulières" disabled={disabled} value={value.special_clauses} onChange={text("special_clauses")} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panneau complet d'un bail

type Section = "conditions" | "echeances" | "indexation" | "factures" | "versions";

export function LeaseTermsPanel({ orgId, leaseId }: { orgId: string; leaseId: string }) {
  const path = `/api/v1/orgs/${orgId}/pro/leases/${leaseId}/terms`;
  const q = useGet<any>(path);
  const [section, setSection] = useState<Section>("conditions");
  if (q.isLoading) return <Loading />;
  if (!q.data) return <ErrorBox error={q.error} />;
  const v = q.data;
  if (!v.applicable) {
    return (
      <Notice title="Conditions commerciales non applicables">
        Ce bail relève du régime « {REGIMES[v.lease.regime] ?? v.lease.regime} » : il suit les règles de l’habitation. Les conditions commerciales et professionnelles ne s’appliquent qu’aux baux commerciaux ou professionnels, créés comme tels.
      </Notice>
    );
  }
  return (
    <div className="stack">
      <RuleNotice>{v.notice}</RuleNotice>
      <Tabs<Section>
        value={section}
        onChange={setSection}
        items={[
          { value: "conditions", label: "Conditions" },
          { value: "echeances", label: "Échéances" },
          { value: "indexation", label: "Indexation" },
          { value: "factures", label: "Factures" },
          { value: "versions", label: "Versions" },
        ]}
      />
      {section === "conditions" ? <ConditionsSection orgId={orgId} leaseId={leaseId} view={v} /> : null}
      {section === "echeances" ? <DeadlinesSection orgId={orgId} leaseId={leaseId} view={v} /> : null}
      {section === "indexation" ? <IndexationSection orgId={orgId} leaseId={leaseId} view={v} /> : null}
      {section === "factures" ? <InvoicesSection orgId={orgId} leaseId={leaseId} view={v} /> : null}
      {section === "versions" ? <VersionsSection orgId={orgId} leaseId={leaseId} /> : null}
    </div>
  );
}

function ConditionsSection({ orgId, leaseId, view }: { orgId: string; leaseId: string; view: any }) {
  const org = useOrg();
  const invalidate = useInvalidate();
  const { busy, error, run } = useRun();
  const review = useRun();
  const [state, setState] = useState<TermsState>(() => termsFromApi(view.terms, view.default_inventory, view.lease.regime));
  const [reviewer, setReviewer] = useState("");
  useEffect(() => setState(termsFromApi(view.terms, view.default_inventory, view.lease.regime)), [view.terms, view.default_inventory, view.lease.regime]);
  const base = `/api/v1/orgs/${orgId}/pro/leases/${leaseId}`;
  const t = view.terms;
  const l = view.lease;
  return (
    <div className="stack">
      <Card title={`${REGIMES[l.regime]} — ${l.property_label}`}>
        <KeyValues
          items={[
            ["Date d’effet", date(l.start_on)],
            ["Terme", date(l.end_on)],
            ["Loyer mensuel hors taxes", money(l.rent_cents)],
            ["Provision ou forfait de charges", money(l.charges_cents)],
            ["Dépôt de garantie", money(l.deposit_cents)],
            ["TVA", l.vat_rate_bp ? percent(l.vat_rate_bp, 1) : "Non applicable"],
            ["Validation juridique", t ? <Status kind="review" value={t.legal_review_status} /> : "Conditions à saisir"],
            ["Version des conditions", t ? `${t.version}${t.legal_reviewer ? ` · relue par ${t.legal_reviewer}` : ""}` : "—"],
          ]}
        />
        {t ? (
          <div className="row">
            {org.can("operate") ? (
              <Button
                variant="secondaire"
                busy={busy}
                onClick={async () => {
                  const r: any = await run(() => post(`${base}/terms/pdf`), "Projet PDF généré.");
                  if (r) {
                    invalidate(base);
                    window.open(docUrl(r.document_id), "_blank", "noopener");
                  }
                }}
              >
                Générer le projet PDF
              </Button>
            ) : null}
            {(view.documents as any[]).slice(0, 3).map((d) => (
              <a key={d.id} href={docUrl(d.id)} target="_blank" rel="noreferrer" className="small">
                {d.title}
              </a>
            ))}
          </div>
        ) : null}
        {t && t.legal_review_status !== "valide" && org.can("decide") ? (
          <div className="pro-inline-form">
            <Input label="Professionnel du droit qui a relu cette version" value={reviewer} error={fieldError(review.error, "reviewer_name")} onChange={(e) => setReviewer(e.target.value)} />
            <Button
              variant="secondaire"
              busy={review.busy}
              onClick={async () => {
                if (await review.run(() => post(`${base}/terms/review`, { reviewer_name: reviewer }), "Relecture enregistrée.")) invalidate(base);
              }}
            >
              Enregistrer la relecture
            </Button>
          </div>
        ) : null}
      </Card>
      <Card title="Conditions particulières">
        <form
          className="stack"
          onSubmit={async (e) => {
            e.preventDefault();
            if (await run(() => put(`${base}/terms`, termsToBody(state)), "Conditions enregistrées : nouvelle version à faire valider.")) invalidate(base, `/api/v1/orgs/${orgId}/pro`);
          }}
        >
          <TermsFields regime={l.regime} value={state} onChange={setState} error={error} disabled={!org.can("operate")} />
          <ErrorBox error={error && !(error as any).fields?.length ? error : null} />
          {error && (error as any).fields?.length ? <Notice tone="erreur">Certains champs sont à corriger : voir les messages ci-dessus.</Notice> : null}
          {org.can("operate") ? (
            <div>
              <Button type="submit" busy={busy}>
                Enregistrer les conditions
              </Button>
            </div>
          ) : null}
        </form>
      </Card>
    </div>
  );
}

function DeadlinesSection({ orgId, leaseId, view }: { orgId: string; leaseId: string; view: any }) {
  const org = useOrg();
  const invalidate = useInvalidate();
  const quick = useQuick();
  const { busy, error, run } = useRun();
  const base = `/api/v1/orgs/${orgId}/pro`;
  if (!view.terms) return <Notice>Renseignez d’abord les conditions : les échéances en découlent.</Notice>;
  return (
    <Card title="Calendrier contractuel">
      <p className="small muted">Chaque date est calculée à partir des clauses saisies et reliée à sa règle. Les congés et demandes de renouvellement sont des actes formels (lettre recommandée ou commissaire de justice) : Bailivo ne les envoie pas.</p>
      {org.can("operate") ? (
        <div>
          <Button
            variant="secondaire"
            busy={busy}
            onClick={async () => {
              const r: any = await run(() => post(`${base}/leases/${leaseId}/events/generate`), undefined);
              if (r) invalidate(`${base}/leases/${leaseId}`, `${base}/commercial`, `${base}/tasks`);
            }}
          >
            Enregistrer les échéances et créer les rappels
          </Button>
        </div>
      ) : null}
      <ErrorBox error={error} />
      <Table caption="Échéances">
        <thead>
          <tr>
            <th>Date</th>
            <th>Échéance</th>
            <th>Règle</th>
            <th>Suivi</th>
          </tr>
        </thead>
        <tbody>
          {(view.deadlines as any[]).map((d) => (
            <tr key={`${d.kind}-${d.due_on}`}>
              <td>
                {date(d.due_on)}
                {d.past ? <span className="block small muted">Passée</span> : null}
              </td>
              <td>
                <strong className="block">{EVENT_KINDS[d.kind] ?? d.kind}</strong>
                <span className="small">{d.label}</span>
              </td>
              <td>
                <details>
                  <summary className="small">Voir la règle</summary>
                  <p className="pro-rule">{d.rule}</p>
                </details>
              </td>
              <td>
                {d.event_id ? (
                  <>
                    <Status kind="event" value={d.status} />
                    {d.task_id ? <span className="block small muted">Rappel dans les tâches</span> : null}
                    {org.can("operate") && d.status === "a_venir" ? (
                      <Button variant="discret" small onClick={async () => (await quick(() => patch(`${base}/lease-events/${d.event_id}`, { status: "traite" }), "Échéance traitée.")) && invalidate(`${base}/leases/${leaseId}`)}>
                        Marquer traitée
                      </Button>
                    ) : null}
                  </>
                ) : (
                  <span className="small muted">{d.past ? "—" : "Non enregistrée"}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

function IndexationSection({ orgId, leaseId, view }: { orgId: string; leaseId: string; view: any }) {
  const org = useOrg();
  const invalidate = useInvalidate();
  const sim = useRun();
  const apply = useRun();
  const [form, setForm] = useState({ new_index: "", new_quarter: "", effective_on: today() });
  const [result, setResult] = useState<any>(null);
  const base = `/api/v1/orgs/${orgId}/pro/leases/${leaseId}`;
  const t = view.terms;
  if (!t || t.index_kind === "aucun") return <Notice>Le bail ne comporte pas de clause d’indexation enregistrée.</Notice>;
  return (
    <div className="stack">
      <Card title={`Indexation — ${t.index_kind.toUpperCase()}`}>
        <KeyValues
          items={[
            ["Indice de base", `${t.index_base_value ?? "—"} (${t.index_base_quarter || "trimestre à préciser"})`],
            ["Loyer actuel hors taxes", money(view.lease.rent_cents)],
          ]}
        />
        <p className="small muted">Saisissez la valeur officielle publiée par l’Insee pour le trimestre prévu au bail. Bailivo ne récupère pas encore les indices automatiquement.</p>
        <div className="form-row">
          <Input label="Nouvelle valeur de l’indice" inputMode="decimal" value={form.new_index} error={fieldError(sim.error, "new_index") ?? fieldError(apply.error, "new_index")} onChange={(e) => setForm({ ...form, new_index: e.target.value })} />
          <Input label="Trimestre de cette valeur" value={form.new_quarter} placeholder="2027-T1" error={fieldError(apply.error, "new_quarter")} onChange={(e) => setForm({ ...form, new_quarter: e.target.value })} />
          <Input label="Date d’effet" type="date" value={form.effective_on} error={fieldError(apply.error, "effective_on")} onChange={(e) => setForm({ ...form, effective_on: e.target.value })} />
        </div>
        <ErrorBox error={sim.error && !fieldError(sim.error, "new_index") ? sim.error : null} />
        <ErrorBox error={apply.error && !(apply.error as any).fields?.length ? apply.error : null} />
        <div className="row">
          <Button
            variant="secondaire"
            busy={sim.busy}
            onClick={async () => {
              const r = await sim.run(() => post(`${base}/indexation/simulate`, { new_index: form.new_index }));
              if (r) setResult(r);
            }}
          >
            Calculer
          </Button>
          {result && org.can("decide") ? (
            <Button
              busy={apply.busy}
              onClick={async () => {
                if (await apply.run(() => post(`${base}/indexation/apply`, form), "Loyer indexé.")) {
                  setResult(null);
                  invalidate(base);
                }
              }}
            >
              Appliquer le nouveau loyer
            </Button>
          ) : null}
        </div>
        {result ? (
          <Notice tone={result.warning ? "attention" : "info"} title={`Nouveau loyer : ${money(result.result.new_rent_cents)} (${result.result.variation_bp >= 0 ? "+" : ""}${percent(result.result.variation_bp, 2)})`}>
            <span className="block mono">{result.result.explanation}</span>
            {result.warning ? <span className="block">{result.warning}</span> : null}
            <span className="block small">{result.source_note}</span>
          </Notice>
        ) : null}
      </Card>
      <Card title="Historique">
        {!view.indexations.length ? <p className="muted">Aucune indexation appliquée.</p> : null}
        {view.indexations.length ? (
          <Table caption="Indexations">
            <thead>
              <tr>
                <th>Effet</th>
                <th>Indices</th>
                <th className="num">Ancien loyer</th>
                <th className="num">Nouveau loyer</th>
                <th>Par</th>
              </tr>
            </thead>
            <tbody>
              {view.indexations.map((i: any) => (
                <tr key={i.id}>
                  <td>{date(i.effective_on)}</td>
                  <td className="small">
                    {(i.base_value_centi / 100).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} ({i.base_quarter}) → {(i.new_value_centi / 100).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} ({i.new_quarter})
                  </td>
                  <td className="num">{money(i.old_rent_cents)}</td>
                  <td className="num">{money(i.new_rent_cents)}</td>
                  <td className="small">{i.applied_by_name ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : null}
      </Card>
    </div>
  );
}

export function InvoiceTable({ items, orgId, onChange }: { items: any[]; orgId: string; onChange: () => void }) {
  const org = useOrg();
  const quick = useQuick();
  const [reason, setReason] = useState<Record<string, string>>({});
  return (
    <Table caption="Factures et avoirs">
      <thead>
        <tr>
          <th>Numéro</th>
          <th>Émise le</th>
          <th>Période</th>
          <th className="num">HT</th>
          <th className="num">TVA</th>
          <th className="num">TTC</th>
          <th>Statut</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {items.map((i) => (
          <tr key={i.id}>
            <td className="mono">
              {i.number}
              {i.property_label ? <span className="block small muted">{i.property_label}</span> : null}
            </td>
            <td>{date(i.issue_date)}</td>
            <td className="small">{i.period_start ? `${date(i.period_start)} – ${date(i.period_end)}` : "—"}</td>
            <td className="num">{money(i.total_ht_cents)}</td>
            <td className="num">{money(i.total_vat_cents)}</td>
            <td className="num">{money(i.total_ttc_cents)}</td>
            <td>{i.kind === "avoir" ? "Avoir" : <Status kind="invoice" value={i.status} />}</td>
            <td className="right">
              <div className="stack-sm">
                {i.document_id ? (
                  <a href={docUrl(i.document_id)} target="_blank" rel="noreferrer">
                    PDF
                  </a>
                ) : null}
                {i.kind === "facture" && i.status === "emise" && org.can("finance") ? (
                  <div className="pro-inline-form">
                    <input aria-label={`Motif de l’avoir pour ${i.number}`} placeholder="Motif de l’avoir" value={reason[i.id] ?? ""} onChange={(e) => setReason({ ...reason, [i.id]: e.target.value })} />
                    <Button
                      variant="discret"
                      small
                      disabled={!reason[i.id]?.trim()}
                      onClick={async () => (await quick(() => post(`/api/v1/orgs/${orgId}/pro/invoices/${i.id}/credit-note`, { reason: reason[i.id] }), "Avoir émis.")) && onChange()}
                    >
                      Émettre un avoir
                    </Button>
                  </div>
                ) : null}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function InvoicesSection({ orgId, leaseId, view }: { orgId: string; leaseId: string; view: any }) {
  const org = useOrg();
  const q = useGet<any>(`/api/v1/orgs/${orgId}/pro/invoices?lease_id=${leaseId}`);
  const invalidate = useInvalidate();
  const { busy, error, run } = useRun();
  const [form, setForm] = useState({ period_start: "", period_end: "", due_date: "" });
  const refresh = () => invalidate(`/api/v1/orgs/${orgId}/pro/invoices`);
  return (
    <Card title="Factures du bail">
      <Notice>
        Une facture n’est pas une quittance : la quittance n’est délivrée qu’après règlement intégral. {view.terms?.vat_option ? "TVA appliquée sur option du bailleur." : "Sans option enregistrée, aucune TVA n’est facturée."}
      </Notice>
      {q.data?.e_invoicing_note ? <p className="small muted">{q.data.e_invoicing_note}</p> : null}
      {q.isLoading ? <Loading /> : null}
      {q.data?.items.length ? <InvoiceTable items={q.data.items} orgId={orgId} onChange={refresh} /> : <p className="muted">Aucune facture pour ce bail.</p>}
      {org.can("finance") && view.terms ? (
        <form
          className="stack"
          onSubmit={async (e) => {
            e.preventDefault();
            if (await run(() => post(`/api/v1/orgs/${orgId}/pro/invoices`, { lease_id: leaseId, ...form, due_date: form.due_date || null }), "Facture émise.")) refresh();
          }}
        >
          <h3>Émettre une facture</h3>
          <p className="small muted">Le loyer tient compte de la franchise, des paliers et de la périodicité ({view.terms.payment_period === "trimestriel" ? "trimestrielle" : "mensuelle"}) saisis dans les conditions. Prochain numéro : suite continue de l’espace.</p>
          <div className="form-row">
            <Input label="Début de période" type="date" required value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} />
            <Input label="Fin de période" type="date" required value={form.period_end} error={fieldError(error, "period_end")} onChange={(e) => setForm({ ...form, period_end: e.target.value })} />
            <Input label="Échéance" type="date" value={form.due_date} error={fieldError(error, "due_date")} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
          </div>
          <ErrorBox error={error && !(error as any).fields?.length ? error : null} />
          <div>
            <Button type="submit" variant="secondaire" busy={busy}>
              Émettre la facture
            </Button>
          </div>
        </form>
      ) : null}
    </Card>
  );
}

function VersionsSection({ orgId, leaseId }: { orgId: string; leaseId: string }) {
  const q = useGet<any>(`/api/v1/orgs/${orgId}/pro/leases/${leaseId}/terms/history`);
  return (
    <Card title="Versions des conditions">
      {q.isLoading ? <Loading /> : null}
      <ul className="list-plain">
        {(q.data?.items ?? []).map((h: any) => (
          <li key={h.version}>
            <strong>Version {h.version}</strong> · {dateTime(h.created_at)}
            {h.created_by_name ? ` · ${h.created_by_name}` : ""}
            <details>
              <summary className="small">Voir le contenu enregistré</summary>
              <KeyValues
                items={[
                  ["Preneur", h.snapshot.tenant_company],
                  ["Destination", h.snapshot.activity],
                  ["Durée", `${h.snapshot.duration_months} mois`],
                  ["Indexation", h.snapshot.index_kind === "aucun" ? "Aucune" : `${String(h.snapshot.index_kind).toUpperCase()} ${h.snapshot.index_base_quarter}`],
                  ["TVA", h.snapshot.vat_option ? percent(h.snapshot.vat_rate_bp, 1) : "Sans option"],
                  ["Relecture", h.snapshot.legal_review_status === "valide" ? "Relue" : "À faire valider"],
                ]}
              />
            </details>
          </li>
        ))}
      </ul>
    </Card>
  );
}
